import { useChat, type Message } from "@ai-sdk/react";
import { AccessHeadersHolder } from "@app/repositories/AccessHeadersHolder";

type UseChatParams = Parameters<typeof useChat>[0];
type UseChatReturn = ReturnType<typeof useChat>;
type ReloadType = UseChatReturn["reload"];
type AppendType = UseChatReturn["append"];
type SubmitParamOptions = Parameters<HandleSubmitType>[1];
type ReloadParamOptions = Parameters<ReloadType>[number];
type AppendParamOptions = Parameters<AppendType>[1];

export function useChatWithAuthHeaders(params: UseChatParams) {
    const {
        handleSubmit: submitWithoutAuth,
        reload: reloadWithoutAuth,
        append: appendWithoutAuth,
        ...rest
    } = useChat(params);

    const handleSubmit = async (
        event: SubmitParamEvent,
        opts?: SubmitParamOptions,
    ) => {
        const headers = (await AccessHeadersHolder.getHeaders()) as Record<
            string,
            string
        >;

        submitWithoutAuth(event, {
            ...opts,
            headers: {
                ...headers,
                ...opts?.headers,
            },
        });
    };

    const append = async (message: Message, opts?: AppendParamOptions) => {
        const headers = (await AccessHeadersHolder.getHeaders()) as Record<
            string,
            string
        >;

        appendWithoutAuth(message, {
            ...opts,
            headers: {
                ...headers,
                ...opts?.headers,
            },
        });
    };

    const reload = async (opts?: ReloadParamOptions) => {
        const headers = (await AccessHeadersHolder.getHeaders()) as Record<
            string,
            string
        >;

        reloadWithoutAuth({
            ...opts,
            headers: {
                ...headers,
                ...opts?.headers,
            },
        });
    };

    return { ...rest, handleSubmit, reload, append };
}

export function makeApiUrl(chatAgent: string): string {
    const baseUrl = import.meta.env.VITE_REACT_APP_REST_URL;

    if (!baseUrl) {
        throw new Error("VITE_REACT_APP_REST_URL is not defined.");
    }

    return new URL(`ai/chat/${chatAgent}`, baseUrl).href;
}

export type HandleSubmitType = UseChatReturn["handleSubmit"];
export type SubmitParamEvent = Parameters<HandleSubmitType>[0];
export type MessageType = Message;
export type AttachmentType = NonNullable<
    Message["experimental_attachments"]
>[number];
